/**
 * Sticky buy bar — floating pill pinned near the bottom of the viewport on
 * the product page (Koala-style). Left to right: section anchor links, a
 * hairline divider, product name + variant/price, and an add-to-cart CTA.
 *
 * - Shows once the buy box (lucky-buy-box, sauna-kit-builder, or the
 *   standard buy buttons) scrolls out of the viewport; hides when it
 *   returns or the footer enters.
 * - The CTA puppet-clicks the buy box's add button so the real add-to-cart
 *   flow (cart drawer, events, error states) is reused.
 * - Variant title + price stay bound to the variant selection: instant sync
 *   from the buy box's hidden input / picker radios, authoritative sync
 *   from ProductSelectEvent.
 * - Anchor links resolve their targets lazily and hide when a target section
 *   doesn't exist on the page; an IntersectionObserver drives active state.
 *
 * Scrolling caveats this file works around:
 * - The theme scrolls .page-wrapper (html/body are overflow:hidden), so all
 *   positions are computed against that container, never the window.
 * - Scrolling is animated manually via rAF with per-frame
 *   `behavior: 'instant'` steps, with a watchdog that jumps straight to the
 *   target when frames don't arrive (hidden/prerendered pages throttle both
 *   rAF and native smooth scrolling).
 * - The product-details column is position:sticky — scrollIntoView on
 *   elements inside it lands on their *stuck* position, so those targets
 *   resolve to their containing section instead.
 */
import { StandardEvents } from '@shopify/events';

/**
 * Format `cents` like `sample` (e.g. "€30,95", "$1,234.00"), keeping its
 * currency symbol and decimal/thousands separators.
 * @param {string} sample
 * @param {number} cents
 * @param {boolean} [trimZeros] drop ",00"/".00" on whole amounts
 */
const formatLike = (sample, cents, trimZeros = false) => {
  const commaDecimal = /\d,\d{2}(?!\d)/.test(sample);
  let [int, dec] = (Math.round(cents) / 100).toFixed(2).split('.');
  int = int.replace(/\B(?=(\d{3})+(?!\d))/g, commaDecimal ? '.' : ',');
  const amount = trimZeros && dec === '00' ? int : int + (commaDecimal ? ',' : '.') + dec;
  return sample.replace(/\d(?:[\d.,]*\d)?/, amount);
};

class StickyBuyBar extends HTMLElement {
  /** @type {AbortController} */
  #abort = new AbortController();
  /** @type {IntersectionObserver | null} */
  #buyBoxObserver = null;
  /** @type {IntersectionObserver | null} */
  #footerObserver = null;
  /** @type {IntersectionObserver | null} */
  #sectionObserver = null;
  /** @type {Array<{id: string, title: string, price: string, compare: string | null, default: boolean}>} */
  #variants = [];
  /** @type {Element | null} */
  #buyBox = null;
  #hiddenByFooter = false;
  #pendingAdd = false;
  /** @type {number | null} */
  #resetTimer = null;
  /** @type {number | null} */
  #scrollRaf = null;
  /** @type {number | null} */
  #scrollWatchdog = null;

  connectedCallback() {
    const { signal } = this.#abort;

    try {
      this.#variants = JSON.parse(this.querySelector('[data-sbb-variants]')?.textContent || '[]');
    } catch {
      this.#variants = [];
    }

    this.#resolveLinks();
    this.#syncVariant();

    this.addEventListener('click', this.#onClick, { signal });

    document.addEventListener('change', this.#onPickerChange, { signal, capture: true });
    document.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartSettled, { signal });
    document.addEventListener(StandardEvents.cartError, this.#onCartSettled, { signal });

    // In the theme editor, show the bar immediately so it can be styled.
    if (window.Shopify?.designMode) {
      this.#show();
      return;
    }

    this.#setupVisibilityObservers();
    this.#setupActiveLinkObserver();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#buyBoxObserver?.disconnect();
    this.#footerObserver?.disconnect();
    this.#sectionObserver?.disconnect();
    if (this.#resetTimer) clearTimeout(this.#resetTimer);
    if (this.#scrollRaf) cancelAnimationFrame(this.#scrollRaf);
    if (this.#scrollWatchdog) clearTimeout(this.#scrollWatchdog);
  }

  get #bar() {
    return this.querySelector('[data-sbb-bar]');
  }

  // --- Scrolling helpers ----------------------------------------------------

  /** The element that actually scrolls the page (Horizon: .page-wrapper). */
  #scroller() {
    let el = this.parentElement;
    while (el && el !== document.body) {
      const cs = getComputedStyle(el);
      if ((cs.overflowY === 'auto' || cs.overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
        return el;
      }
      el = el.parentElement;
    }
    return document.scrollingElement || document.documentElement;
  }

  /** Document-space top of an element, measured inside the scroll container. */
  #topWithin(el, scroller) {
    return el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
  }

  /** @param {Element} el */
  #stickyAncestor(el) {
    let node = el instanceof Element ? el : null;
    while (node && node !== document.body) {
      if (getComputedStyle(node).position === 'sticky') return node;
      node = node.parentElement;
    }
    return null;
  }

  /**
   * rAF-driven scroll animation using instant per-frame steps. A watchdog
   * jumps straight to the target if no frame arrives (throttled/hidden
   * pages), so the scroll always completes.
   */
  #animateScroll(scroller, to) {
    if (this.#scrollRaf) cancelAnimationFrame(this.#scrollRaf);
    if (this.#scrollWatchdog) clearTimeout(this.#scrollWatchdog);

    const from = scroller.scrollTop;
    const max = scroller.scrollHeight - scroller.clientHeight;
    const target = Math.max(0, Math.min(max, to));
    const delta = target - from;
    if (Math.abs(delta) < 2) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      scroller.scrollTo({ top: target, behavior: 'instant' });
      return;
    }

    const duration = Math.min(700, 300 + Math.abs(delta) * 0.12);
    const start = performance.now();
    const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    let started = false;

    const step = (now) => {
      started = true;
      const t = Math.min(1, (now - start) / duration);
      scroller.scrollTo({ top: from + delta * ease(t), behavior: 'instant' });
      this.#scrollRaf = t < 1 ? requestAnimationFrame(step) : null;
    };
    this.#scrollRaf = requestAnimationFrame(step);

    this.#scrollWatchdog = setTimeout(() => {
      if (started) return;
      if (this.#scrollRaf) cancelAnimationFrame(this.#scrollRaf);
      this.#scrollRaf = null;
      scroller.scrollTo({ top: target, behavior: 'instant' });
    }, 200);
  }

  /**
   * Scrolls so the target is on screen. Elements inside the sticky details
   * column can't be targeted directly: for those, land at the containing
   * section instead — its start for targets near the column top (variant
   * picker), its end for targets at the column bottom (details accordion).
   *
   * @param {Element} target
   * @param {'start' | 'end'} align - which edge of a sticky section to land on
   */
  #scrollToTarget(target, align = 'start') {
    const scroller = this.#scroller();
    const viewport = scroller.clientHeight || window.innerHeight;

    let top;
    const sticky = this.#stickyAncestor(target);
    if (sticky) {
      const section = target.closest('.shopify-section') || sticky.parentElement;
      const sectionTop = this.#topWithin(section, scroller);
      top = align === 'end' ? sectionTop + section.offsetHeight - viewport : sectionTop;
    } else {
      top = this.#topWithin(target, scroller) - 24;
    }

    this.#animateScroll(scroller, top);
  }

  // --- Visibility -----------------------------------------------------------

  #setupVisibilityObservers() {
    this.#buyBox =
      document.querySelector('lucky-buy-box') ||
      document.querySelector('sauna-kit-builder') ||
      document.querySelector('.buy-buttons-block') ||
      document.querySelector('product-form-component');
    if (!this.#buyBox) return;

    this.#buyBoxObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;
      if (!entry.isIntersecting) {
        // Only show once the buy box has been scrolled PAST (above the
        // viewport), not while it's still further down the page.
        const rect = entry.target.getBoundingClientRect();
        if ((rect.bottom < 0 || rect.top < 0) && !this.#hiddenByFooter) this.#show();
      } else {
        this.#hiddenByFooter = false;
        this.#hide();
      }
    });
    this.#buyBoxObserver.observe(this.#buyBox);

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#footerObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;
        if (entry.isIntersecting) {
          this.#hiddenByFooter = true;
          this.#hide();
        } else if (this.#hiddenByFooter) {
          this.#hiddenByFooter = false;
          const rect = this.#buyBox.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) this.#show();
        }
      },
      { rootMargin: '80px 0px 0px 0px' }
    );
    this.#footerObserver.observe(footer);
  }

  #show() {
    this.#bar?.setAttribute('data-visible', 'true');
  }

  #hide() {
    this.#bar?.setAttribute('data-visible', 'false');
  }

  // --- Anchor links ---------------------------------------------------------

  /**
   * Resolves each link's target selector; hides links whose target is absent.
   * Only visible content elements qualify — selectors like [id*=review] must
   * never latch onto <script>/<style> tags or hidden nodes (e.g. Shopify's
   * preview-bar data script).
   */
  #resolveLinks() {
    /** @type {Array<{link: HTMLElement, target: Element}>} */
    this.links = [];
    this.querySelectorAll('[data-sbb-target]').forEach((link) => {
      let target = null;
      try {
        target = [...document.querySelectorAll(link.dataset.sbbTarget)].find(
          (el) =>
            !this.contains(el) &&
            !el.matches('script, style, link, template, meta') &&
            el.getClientRects().length > 0
        );
      } catch {
        target = null;
      }
      if (target) {
        this.links.push({ link, target });
      } else {
        link.hidden = true;
      }
    });
  }

  #setupActiveLinkObserver() {
    if (!this.links.length) return;
    this.#sectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const match = this.links.find(({ target }) => target === entry.target);
          if (!match) continue;
          this.links.forEach(({ link }) => link.removeAttribute('data-active'));
          match.link.setAttribute('data-active', 'true');
        }
      },
      // A generous band across the middle of the viewport: a section is
      // "active" while it crosses it.
      { rootMargin: '-15% 0px -25% 0px' }
    );
    this.links.forEach(({ target }) => this.#sectionObserver.observe(target));
  }

  // --- Events ---------------------------------------------------------------

  #onClick = (event) => {
    const anchor = event.target.closest('[data-sbb-target]');
    if (anchor) {
      const match = this.links?.find(({ link }) => link === anchor);
      if (match) {
        event.preventDefault();
        // Details/FAQ content sits at the bottom of the sticky column, so
        // land on the section's end for sticky targets.
        this.#scrollToTarget(match.target, 'end');
      }
      return;
    }

    if (event.target.closest('[data-sbb-variant]')) {
      const picker =
        document.querySelector('lucky-buy-box') ||
        document.querySelector('variant-picker') ||
        document.querySelector('.variant-picker');
      const target = picker || this.#buyBox;
      // The selector sits near the top of the sticky column — land on the
      // section's start so it's on screen.
      if (target) this.#scrollToTarget(target, 'start');
      return;
    }

    if (event.target.closest('[data-sbb-cta]')) this.#addToCart();
  };

  #addToCart() {
    const buyBoxButton = document.querySelector('lucky-buy-box [data-lbb-add]');
    const kitButton = document.querySelector('sauna-kit-builder [data-kit-add]');
    const formButton = document.querySelector('product-form-component [ref="addToCartButton"]');
    const target = buyBoxButton || kitButton || formButton;

    if (!target) {
      if (this.#buyBox) this.#scrollToTarget(this.#buyBox, 'start');
      return;
    }

    this.#pendingAdd = true;
    this.#setCtaLabel(this.dataset.addingLabel || 'Adding…');
    target.click();
  }

  /** Cart add settled (success or error) — reflect the outcome on the CTA. */
  #onCartSettled = (event) => {
    if (!this.#pendingAdd) return;
    this.#pendingAdd = false;

    const settle = (ok) => {
      this.#setCtaLabel(ok ? this.dataset.addedLabel || '✓ Added' : this.dataset.errorLabel || 'Unavailable');
      if (this.#resetTimer) clearTimeout(this.#resetTimer);
      this.#resetTimer = setTimeout(() => this.#setCtaLabel(null), 2000);
    };

    if (event.type === StandardEvents.cartError) {
      settle(false);
    } else if ('promise' in event && event.promise instanceof Promise) {
      event.promise.then(({ detail }) => settle(!detail?.didError)).catch(() => settle(false));
    } else {
      settle(true);
    }
  };

  /** @param {string | null} label - null restores the default label */
  #setCtaLabel(label) {
    const labelEl = this.querySelector('[data-sbb-cta-label]');
    if (!labelEl) return;
    labelEl.textContent = label ?? this.dataset.ctaLabel ?? 'Add to Cart';
  }

  // --- Variant binding ------------------------------------------------------

  /** Instant (optimistic) sync when the buy box or a variant picker changes. */
  #onPickerChange = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('variant-picker, lucky-buy-box')) return;
    setTimeout(() => this.#syncVariant(), 0);
  };

  /** Authoritative sync once the picker's fetch resolves the real variant. */
  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    event.promise
      ?.then(({ detail }) => {
        const variant = detail?.resource;
        if (variant?.id) this.#applyVariant(String(variant.id), true);
      })
      .catch(() => {});
  };

  #syncVariant() {
    // Priority: the buy box's own selection, then a checked picker radio,
    // then the URL param.
    let id = document.querySelector('lucky-buy-box [data-lbb-variant-id]')?.value;
    let explicit = Boolean(id);

    if (!id) {
      const checked = document.querySelector('variant-picker input[type="radio"][data-variant-id]:checked');
      id = checked?.dataset.variantId;
      explicit = Boolean(id);
    }

    if (!id) {
      const fromUrl = new URL(window.location.href).searchParams.get('variant');
      if (fromUrl) {
        id = fromUrl;
        explicit = true;
      }
    }

    this.#applyVariant(id, explicit);
  }

  /**
   * @param {string | undefined} id
   * @param {boolean} explicit - whether the shopper actually picked this variant
   */
  #applyVariant(id, explicit) {
    const variant =
      this.#variants.find((v) => String(v.id) === String(id)) ||
      this.#variants.find((v) => v.default) ||
      this.#variants[0];
    if (!variant) return;

    const nameEl = this.querySelector('[data-sbb-variant]');
    const priceEl = this.querySelector('[data-sbb-price]');
    const rrpEl = this.querySelector('[data-sbb-rrp]');

    if (nameEl) {
      // With no selection at all, invite the shopper to choose.
      nameEl.textContent = explicit ? variant.title : this.dataset.variantPrompt || variant.title;
    }
    if (priceEl) priceEl.textContent = variant.price;
    if (rrpEl) {
      let rrp = variant.compare || '';
      if (!rrp && this.dataset.rrpFallback) {
        if (this.hasAttribute('data-rrp-convert')) {
          // The fallback RRP is typed in the shop currency ("$59.00"); convert it.
          const rate = parseFloat(window.Shopify?.currency?.rate);
          const base = parseFloat(this.dataset.rrpFallback.replace(/[^0-9.]/g, ''));
          rrp = rate > 0 && base > 0 ? formatLike(variant.price, Math.round(base * 100 * rate)) : '';
        } else {
          rrp = this.dataset.rrpFallback;
        }
      }
      rrpEl.textContent = rrp;
      rrpEl.hidden = !rrp || rrp === variant.price;
    }
  }
}

if (!customElements.get('sticky-buy-bar')) {
  customElements.define('sticky-buy-bar', StickyBuyBar);
}
